import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import JdUpload from "../components/JdUpload";
import { toast } from "sonner";

export default function RaiseRequisitionPublic() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    submitter_name: "",
    submitter_email: "",
    submitter_team: "",
    note: "",
    title: "",
    department: "",
    location: "",
    employment_type: "full_time",
    headcount: 1,
    experience_min: 0,
    experience_max: 0,
    ctc_min: "",
    ctc_max: "",
    skills: "",
    description: "",
    urgency: "medium",
    jd_file_b64: "",
    jd_filename: "",
    jd_mime: "",
  });

  useEffect(() => {
    api
      .get(`/public/requisition-invites/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Link unavailable"));
  }, [token]);

  const change = (k) => (e) => {
    const v = e.target.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.experience_max) > 0 && Number(form.experience_min) > Number(form.experience_max)) {
      return toast.error("Minimum experience cannot exceed maximum experience");
    }
    if (form.ctc_min !== "" && form.ctc_max !== "" && Number(form.ctc_min) > Number(form.ctc_max)) {
      return toast.error("Minimum CTC cannot exceed maximum CTC");
    }
    setBusy(true);
    try {
      await api.post(`/public/requisition-invites/${token}/submit`, {
        ...form,
        ctc_min: form.ctc_min === "" ? null : Number(form.ctc_min),
        ctc_max: form.ctc_max === "" ? null : Number(form.ctc_max),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Link status</div>
          <h1 className="font-serif text-4xl text-ink mt-2">This link isn't active.</h1>
          <p className="text-quiet mt-3 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!invite) return <div className="p-8 text-quiet">Loading…</div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white border border-rule p-8 fade-up" data-testid="raise-success">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Submitted</div>
          <h1 className="font-serif text-4xl text-ink mt-2 leading-none">Thank you.</h1>
          <p className="text-quiet mt-4 text-sm">
            Your requisition request for <span className="text-ink">{form.title}</span> has been sent to{" "}
            <span className="text-ink">{invite.inviter_name}</span>. HR will review it within 3 working days and
            follow up at <span className="font-mono">{form.submitter_email || invite.inviter_email}</span>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="raise-requisition-public">
      <div className="border-b border-rule bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
            Invited by {invite.inviter_name} · {invite.inviter_email}
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl text-ink mt-3 leading-[0.95]">Raise a requisition.</h1>
          <p className="text-quiet mt-4 text-sm max-w-xl">
            Fill the request below. {invite.label ? <>Tagged as <span className="font-mono">{invite.label}</span>. </> : null}
            HR will review within 3 working days and reach out if any details are missing.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-6 lg:px-12 py-10 space-y-8">
        {/* Submitter */}
        <section className="bg-white border border-rule p-6 space-y-4">
          <h2 className="font-serif text-2xl text-ink">Your details</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Your name">
              <input data-testid="raise-submitter-name" required value={form.submitter_name} onChange={change("submitter_name")} className={inp} />
            </Field>
            <Field label="Your work email">
              <input data-testid="raise-submitter-email" required type="email" value={form.submitter_email} onChange={change("submitter_email")} className={inp} />
            </Field>
          </div>
          <Field label="Team / department (optional)">
            <input data-testid="raise-submitter-team" value={form.submitter_team} onChange={change("submitter_team")} className={inp} />
          </Field>
          <Field label="Note to HR (optional)">
            <textarea data-testid="raise-note" rows={2} value={form.note} onChange={change("note")} className={inp} />
          </Field>
        </section>

        {/* Role */}
        <section className="bg-white border border-rule p-6 space-y-4">
          <h2 className="font-serif text-2xl text-ink">The role</h2>
          <Field label="Role title">
            <input data-testid="raise-title" required value={form.title} onChange={change("title")} className={inp} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Department">
              <input data-testid="raise-department" required value={form.department} onChange={change("department")} className={inp} />
            </Field>
            <Field label="Location">
              <input data-testid="raise-location" required value={form.location} onChange={change("location")} className={inp} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Headcount">
              <input data-testid="raise-headcount" type="number" min={1} value={form.headcount} onChange={change("headcount")} className={inp} />
            </Field>
            <Field label="Employment type">
              <select data-testid="raise-employment" value={form.employment_type} onChange={change("employment_type")} className={inp}>
                <option value="full_time">Full time</option>
                <option value="contract">Contract</option>
                <option value="intern">Intern</option>
              </select>
            </Field>
            <Field label="Urgency">
              <select data-testid="raise-urgency" value={form.urgency} onChange={change("urgency")} className={inp}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Min experience (yrs)">
              <input data-testid="raise-exp-min" type="number" min={0} value={form.experience_min} onChange={change("experience_min")} className={inp} />
            </Field>
            <Field label="Max experience (yrs)">
            <input data-testid="raise-exp-max" type="number" min={form.experience_min || 0} value={form.experience_max} onChange={change("experience_max")} className={inp} />
          </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="CTC min (LPA)">
              <input data-testid="raise-ctc-min" type="number" min={0} max={form.ctc_max || undefined} value={form.ctc_min} onChange={change("ctc_min")} className={inp} />
            </Field>
            <Field label="CTC max (LPA)">
              <input data-testid="raise-ctc-max" type="number" min={form.ctc_min || 0} value={form.ctc_max} onChange={change("ctc_max")} className={inp} />
            </Field>
          </div>
          <Field label="Skills (comma separated)">
            <input data-testid="raise-skills" required value={form.skills} onChange={change("skills")} placeholder="React, TypeScript" className={inp} />
          </Field>
          <JdUpload form={form} setForm={setForm} />
          <Field label="Job description (or rely on uploaded JD above)">
            <textarea data-testid="raise-description" rows={8} value={form.description} onChange={change("description")} className={inp} placeholder="Type a short description here, or upload the formal JD file above." />
          </Field>
        </section>

        <button
          data-testid="raise-submit"
          type="submit"
          disabled={busy}
          className="w-full bg-klein hover:bg-klein-hover text-white px-5 py-3 text-sm font-medium disabled:opacity-50"
        >
          {busy ? "Submitting…" : "Send to HR →"}
        </button>
      </form>
    </div>
  );
}

const inp = "w-full bg-transparent border border-rule px-3 py-2 focus:outline-none focus:border-klein focus:ring-1 focus:ring-klein font-sans text-sm";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
